// frontend/src/pages/BlogPost.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Eye, Tag, ArrowLeft, Share2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost]       = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios.get(`/api/blog/${slug}`)
      .then(res => {
        setPost(res.data);
        // fetch related (same category)
        return axios.get('/api/blog', { params: { category: res.data.category, limit: 3 } });
      })
      .then(res => setRelated(res.data.posts.filter(p => p.slug !== slug).slice(0, 3)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied!');
    }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>;
  if (!post)   return <div style={{ textAlign: 'center', padding: 80 }}><h2>Post not found</h2><Link to="/blogs" className="btn btn-primary" style={{ marginTop: 16, display: 'inline-flex' }}>← Back to Blog</Link></div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 20px' }}>
      <div className="article-layout-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 40, alignItems: 'start' }}>

        {/* Main content */}
        <article>
          <Link to="/blogs" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', textDecoration: 'none', marginBottom: 24, fontSize: '0.9rem' }}>
            <ArrowLeft size={16} /> Back to Blog
          </Link>

          {/* Tags + category */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <span style={{ background: '#E8F1FF', color: 'var(--primary)', padding: '4px 12px', borderRadius: 50, fontSize: '0.8rem', fontWeight: 700 }}>
              {post.category}
            </span>
            {post.tags && post.tags.map(tag => (
              <Link key={tag} to={`/blogs?tag=${tag}`} style={{ background: '#f3f4f6', color: 'var(--text-muted)', padding: '4px 12px', borderRadius: 50, fontSize: '0.8rem', textDecoration: 'none' }}>
                #{tag}
              </Link>
            ))}
          </div>

          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.6rem,4vw,2.4rem)', lineHeight: 1.25, marginBottom: 16 }}>
            {post.title}
          </h1>

          {/* Meta */}
          <div style={{ display: 'flex', gap: 20, color: '#94a3b8', fontSize: '0.85rem', marginBottom: 28, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Calendar size={14} />
              {new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Eye size={14} /> {post.views} views
            </span>
            {post.author && post.author.name && <span>By <strong>{post.author.name}</strong></span>}
            <button onClick={handleShare} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>
              <Share2 size={14} /> Share
            </button>
          </div>

          {/* Cover image */}
          {post.coverImage && (
            <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 32 }}>
              <img src={post.coverImage} alt={post.title} style={{ width: '100%', maxHeight: 420, objectFit: 'cover' }} />
            </div>
          )}

        

          {/* Article body */}
          <div
            className="blog-content"
            style={{ lineHeight: 1.8, fontSize: '1.02rem' }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

         

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
              <p style={{ fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Tag size={16} /> Tags
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {post.tags.map(tag => (
                  <Link key={tag} to={`/blogs?tag=${tag}`}
                    style={{ background: '#f3f4f6', color: 'var(--text)', padding: '6px 16px', borderRadius: 50, fontSize: '0.85rem', textDecoration: 'none', fontWeight: 500 }}>
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Sidebar */}
        <aside style={{ position: 'sticky', top: 80 }}>
         

          {related.length > 0 && (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginTop: 20 }}>
              <h3 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1rem', marginBottom: 16 }}>Related Articles</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {related.map(r => (
                  <Link key={r._id} to={`/blogs/${r.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 60, height: 48, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: '#f3f4f6' }}>
                      {r.coverImage
                        ? <img src={r.coverImage} alt={r.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📝</div>}
                    </div>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3, marginBottom: 4 }}>{r.title}</p>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {new Date(r.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Quick links */}
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, marginTop: 20 }}>
            <h3 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1rem', marginBottom: 12 }}>Quick Tools</h3>
            {/* PDF to JPG hidden while it doesn't work: ['🖼️ PDF to JPG', '/convert/pdf-to-jpg'] */}
            {[['🖼️ JPG to PDF', '/convert/jpg-to-pdf'], ['🖼️ Image Convert', '/image-convert'], ['📑 Merge PDF', '/merge-pdf']].map(([label, href]) => (
              <Link key={href} to={href} style={{ display: 'block', padding: '8px 0', color: 'var(--primary)', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>
                {label}
              </Link>
            ))}
          </div>
        </aside>
      </div>

      {/* Blog post content styles */}
      <style>{`
        .blog-content h2 { font-family: Sora, sans-serif; font-size: 1.5rem; margin: 2rem 0 1rem; color: var(--text); }
        .blog-content h3 { font-family: Sora, sans-serif; font-size: 1.2rem; margin: 1.5rem 0 0.8rem; }
        .blog-content p  { margin-bottom: 1.2rem; color: #374151; }
        .blog-content ul, .blog-content ol { margin: 1rem 0 1rem 1.5rem; }
        .blog-content li { margin-bottom: 0.4rem; }
        .blog-content img { max-width: 100%; border-radius: 10px; margin: 1rem 0; }
        .blog-content blockquote { border-left: 4px solid var(--primary); padding: 12px 20px; background: #fff8f8; border-radius: 0 8px 8px 0; margin: 1.5rem 0; color: #555; font-style: italic; }
        .blog-content pre { background: #1e293b; color: #e2e8f0; padding: 20px; border-radius: 10px; overflow-x: auto; margin: 1.5rem 0; font-size: 0.9rem; }
        .blog-content code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 0.88em; color: var(--primary); }
        .blog-content pre code { background: none; color: inherit; padding: 0; }
        .blog-content a { color: var(--primary); }
        .blog-content table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; }
        .blog-content th, .blog-content td { border: 1px solid var(--border); padding: 10px 14px; text-align: left; }
        .blog-content th { background: #f8fafc; font-weight: 700; }
        @media (max-width: 768px) {
          article + aside { display: none; }
        }
      `}</style>
    </div>
  );
}
